/* eslint-disable @typescript-eslint/no-redeclare */
import * as React from "react";

import { cn } from "@/lib/utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Drop-in shim for Fluent UI's `TooltipHost`. Wraps shadcn `Tooltip`. The
 * Fluent `content` prop becomes the tooltip body; `directionalHint` is
 * translated to radix's `side` + `align`. A `TooltipProvider` is rendered
 * locally so each `TooltipHost` works standalone, without needing a
 * page-level provider.
 *
 * Children must be a single React element (the same constraint Fluent had).
 * `asChild` is used on the trigger so we don't introduce an extra wrapper.
 */

// Fluent's DirectionalHint is a numeric enum (0..13). Mirror just the values
// we need so call sites that import `DirectionalHint` from the shim or from
// Fluent both keep compiling.
export const DirectionalHint = {
  topLeftEdge: 0,
  topCenter: 1,
  topRightEdge: 2,
  topAutoEdge: 3,
  bottomLeftEdge: 4,
  bottomCenter: 5,
  bottomRightEdge: 6,
  bottomAutoEdge: 7,
  leftTopEdge: 8,
  leftCenter: 9,
  leftBottomEdge: 10,
  rightTopEdge: 11,
  rightCenter: 12,
  rightBottomEdge: 13,
} as const;
export type DirectionalHint = (typeof DirectionalHint)[keyof typeof DirectionalHint];

function mapDirection(
  hint: number | undefined,
): { side: "top" | "right" | "bottom" | "left"; align: "start" | "center" | "end" } {
  switch (hint) {
    case DirectionalHint.topLeftEdge:    return { side: "top",    align: "start"  };
    case DirectionalHint.topCenter:
    case DirectionalHint.topAutoEdge:    return { side: "top",    align: "center" };
    case DirectionalHint.topRightEdge:   return { side: "top",    align: "end"    };
    case DirectionalHint.bottomLeftEdge: return { side: "bottom", align: "start"  };
    case DirectionalHint.bottomRightEdge: return { side: "bottom", align: "end"   };
    case DirectionalHint.bottomAutoEdge: return { side: "bottom", align: "center" };
    case DirectionalHint.leftTopEdge:    return { side: "left",   align: "start"  };
    case DirectionalHint.leftCenter:     return { side: "left",   align: "center" };
    case DirectionalHint.leftBottomEdge: return { side: "left",   align: "end"    };
    case DirectionalHint.rightTopEdge:   return { side: "right",  align: "start"  };
    case DirectionalHint.rightCenter:    return { side: "right",  align: "center" };
    case DirectionalHint.rightBottomEdge: return { side: "right", align: "end"    };
    case DirectionalHint.bottomCenter:
    default:                             return { side: "bottom", align: "center" };
  }
}

export const TooltipDelay = {
  zero: 0,
  medium: 1,
  long: 2,
} as const;
export type TooltipDelay = (typeof TooltipDelay)[keyof typeof TooltipDelay];

export interface FluentTooltipHostProps {
  content?: React.ReactNode;
  tooltipProps?: {
    onRenderContent?: () => React.ReactNode;
    className?: string;
    sideOffset?: number;
    [key: string]: any;
  };
  directionalHint?: number;
  delay?: number;
  closeDelay?: number;
  setAriaDescribedBy?: boolean;
  hostClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  // Drops:
  styles?: any;
  componentRef?: any;
  calloutProps?: {
    gapSpace?: number;
    className?: string;
    [key: string]: any;
  };
  hidden?: boolean;
  onTooltipToggle?: (...args: any[]) => any;
  overflowMode?: any;
  children?: React.ReactNode;
}

const delayMs = (d?: number) => {
  switch (d) {
    case TooltipDelay.zero:   return 0;
    case TooltipDelay.long:   return 800;
    case TooltipDelay.medium: return 300;
    default: return 300;
  }
};

export const TooltipHost = React.forwardRef<HTMLSpanElement, FluentTooltipHostProps>(
  function TooltipHost(props, ref) {
    const {
      content,
      tooltipProps,
      directionalHint,
      delay,
      closeDelay,
      hostClassName,
      className,
      style,
      id,
      children,
      calloutProps,
    } = props;

    // Resolve final tooltip body.
    const body =
      tooltipProps?.onRenderContent?.() ?? content ?? null;

    // No content → just render the children inline (Fluent does the same).
    if (body == null || body === false || body === "") {
      return (
        <span
          ref={ref}
          className={cn(hostClassName, className)}
          style={style}
          id={id}
        >
          {children}
        </span>
      );
    }

    const { side, align } = mapDirection(directionalHint);
    const tooltipClassName = cn(calloutProps?.className, tooltipProps?.className);
    const tooltipSideOffset = tooltipProps?.sideOffset ?? calloutProps?.gapSpace;

    return (
      <TooltipProvider
        delayDuration={delayMs(delay)}
        skipDelayDuration={Math.max(0, closeDelay ?? 0)}
      >
        <Tooltip>
          <TooltipTrigger
            asChild={React.isValidElement(children)}
            id={id}
            className={cn(hostClassName, className)}
            // The trigger expects a button by default; using asChild keeps the
            // original element. When children isn't a single element, fall
            // back to a span wrapper.
          >
            {React.isValidElement(children) ? (
              children
            ) : (
              <span ref={ref} style={style}>
                {children}
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent
            side={side}
            align={align}
            sideOffset={tooltipSideOffset}
            className={tooltipClassName}
          >
            {body}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
TooltipHost.displayName = "TooltipHost";
